import {
  buildVeeraContextualThreadKey,
  type VeeraContextualThreadEntrySurface,
  type VeeraContextualThreadInput,
  type VeeraContextualThreadRole,
  type VeeraContextualThreadSourceRef,
} from "@/lib/veeraContextualThreads";
import type { VeeraContextualEntryTransport } from "../../supabase/functions/_shared/veeraContextualEntry.ts";

type VeeraThreadHydrationRecord = {
  id: string;
  title: string;
  updatedAt?: string;
  sourceLabel?: string | null;
  fixedTitle?: string | null;
  contextualThreadInput?: VeeraContextualThreadInput | null;
  threadKey?: string | null;
  contextualTopicId?: string | null;
  role?: VeeraContextualThreadRole;
  entrySurface?: VeeraContextualThreadEntrySurface;
  primaryEntityId?: string | null;
  topicFingerprint?: string;
  sourceId?: VeeraContextualThreadSourceRef;
  creationOrigin?: "user";
};

type VeeraContextualDisplayEntry = Pick<
  VeeraContextualEntryTransport,
  "threadTitle" | "sourceLabel" | "contextualThreadInput"
>;

function shouldPreserveLocalOnlyThread(thread: VeeraThreadHydrationRecord) {
  return Boolean(
    thread.creationOrigin === "user"
    || thread.fixedTitle
    || thread.sourceLabel
    || thread.contextualThreadInput
    || thread.threadKey
    || thread.contextualTopicId
    || thread.role
    || thread.entrySurface
    || thread.topicFingerprint
    || thread.sourceId,
  );
}

export function mergePersistedVeeraThreads<TThread extends VeeraThreadHydrationRecord>(params: {
  existingThreads: TThread[];
  hydratedThreads: TThread[];
}) {
  const currentById = new Map(params.existingThreads.map((thread) => [thread.id, thread]));
  const merged = params.hydratedThreads.map((thread) => {
    const existing = currentById.get(thread.id);
    if (!existing) return thread;

    return {
      ...thread,
      threadKey: existing.threadKey ?? thread.threadKey ?? null,
      contextualTopicId: existing.contextualTopicId
        ?? thread.contextualTopicId
        ?? existing.contextualThreadInput?.contextualTopicId
        ?? thread.contextualThreadInput?.contextualTopicId
        ?? null,
      role: existing.role
        ?? thread.role
        ?? existing.contextualThreadInput?.role
        ?? thread.contextualThreadInput?.role,
      entrySurface: existing.entrySurface
        ?? thread.entrySurface
        ?? existing.contextualThreadInput?.entrySurface
        ?? thread.contextualThreadInput?.entrySurface,
      primaryEntityId: existing.primaryEntityId
        ?? thread.primaryEntityId
        ?? existing.contextualThreadInput?.primaryEntityId
        ?? thread.contextualThreadInput?.primaryEntityId
        ?? null,
      topicFingerprint: existing.topicFingerprint
        ?? thread.topicFingerprint
        ?? existing.contextualThreadInput?.topicFingerprint
        ?? thread.contextualThreadInput?.topicFingerprint,
      sourceId: existing.sourceId
        ?? thread.sourceId
        ?? existing.contextualThreadInput?.sourceId
        ?? thread.contextualThreadInput?.sourceId
        ?? null,
      title: existing.fixedTitle || thread.title,
      sourceLabel: existing.sourceLabel ?? thread.sourceLabel ?? null,
      fixedTitle: existing.fixedTitle ?? null,
      contextualThreadInput: existing.contextualThreadInput ?? thread.contextualThreadInput ?? null,
    };
  });

  const hydratedIds = new Set(params.hydratedThreads.map((thread) => thread.id));
  const localOnlyThreads = params.existingThreads.filter((thread) => (
    !hydratedIds.has(thread.id) && shouldPreserveLocalOnlyThread(thread)
  ));

  return [...merged, ...localOnlyThreads].sort((left, right) => {
    const leftTime = Date.parse(left.updatedAt || "");
    const rightTime = Date.parse(right.updatedAt || "");
    const leftRank = Number.isNaN(leftTime) ? 0 : leftTime;
    const rightRank = Number.isNaN(rightTime) ? 0 : rightTime;
    return rightRank - leftRank;
  });
}

export function resolveHydratedVeeraActiveThreadId<TThread extends VeeraThreadHydrationRecord>(params: {
  currentActiveThreadId: string | null;
  mergedThreads: TThread[];
}) {
  if (
    params.currentActiveThreadId
    && params.mergedThreads.some((thread) => thread.id === params.currentActiveThreadId)
  ) {
    return params.currentActiveThreadId;
  }

  return params.mergedThreads[0]?.id ?? null;
}

function contextualInputsMatch(
  left: VeeraContextualThreadInput | null | undefined,
  right: VeeraContextualThreadInput | null | undefined,
) {
  if (!left || !right) return false;
  return buildVeeraContextualThreadKey(left) === buildVeeraContextualThreadKey(right);
}

export function resolveActiveVeeraThreadPresentation<
  TThread extends VeeraThreadHydrationRecord,
  TEntry extends VeeraContextualDisplayEntry | null,
>(params: {
  activeThread: TThread | null;
  contextualEntry: TEntry;
}) {
  const usesContextualEntry = contextualInputsMatch(
    params.activeThread?.contextualThreadInput,
    params.contextualEntry?.contextualThreadInput,
  );
  const activeContextualEntry = usesContextualEntry ? params.contextualEntry : null;

  return {
    usesContextualEntry,
    activeContextualEntry,
    fixedTitle: params.activeThread?.fixedTitle ?? activeContextualEntry?.threadTitle ?? null,
    sourceLabel: params.activeThread?.sourceLabel?.trim() || activeContextualEntry?.sourceLabel || null,
  };
}
